import { FC, useContext, useEffect, useState } from "react";
import { SidebarContext } from "../../../../../contexts/SidebarContext";
import SearchBox from "../../../../../layouts/SidebarLayout/Header/SearchBox";
import DataTable from "../../../../../shared/components/dataTable";
import useQuery from "../../../../../shared/components/hooks/useQuery";
import Loader from "../../../../../shared/components/loader/linearProgress";
import NoPermissionContent from "../../../../../shared/components/noPermissionBanner";
import { ZSSelectBox } from "../../../../../shared/components/selectBox";
import { TableViewContainer } from "../../../../../shared/components/tableViewContainer";

const StudentsList: FC = () => {
  const { payload, setPayload } = useContext(SidebarContext);
  const [classOptions, setClassOptions] = useState<any>([]);
  const [divisionOptions, setDivisionOptions] = useState<any>([]);

  // Clear only studentListFilters on unmount
  useEffect(() => {
    return () => {
      setPayload((prev: any) => ({
        ...prev,
        studentListFilters: {
          className: undefined,
          division: undefined,
        },
        skip: 0,
      }));
    };
  }, [setPayload]);

  const { data: classData } = useQuery("/admin/classes");
  useEffect(() => {
    if (classData) {
      const classMap = new Map<string, any>();
      (classData as any).forEach((c: any) => {
        if (!classMap.has(c.displayName)) {
          classMap.set(c.displayName, c);
        }
      });
      const uniqueClasses = Array.from(classMap.values()).map((c: any) => ({
        value: String(c.id),
        label: c.displayName,
      }));
      setClassOptions(uniqueClasses);
    }
  }, [classData]);

  const className = payload.studentListFilters?.className;
  const divisionValue = payload.studentListFilters?.division;

  const { data: divisionData } = useQuery(
    className ? `/admin/students/classes/${className}/divisions` : ""
  );

  useEffect(() => {
    if (Array.isArray(divisionData)) {
      const divisions = (divisionData as any).map((division: string) => ({
        value: division,
        label: division,
      }));
      setDivisionOptions(divisions);

      if (!divisions.find((d: { value: any; }) => d.value === divisionValue)) {
        setPayload((prev: any) => ({
          ...prev,
          studentListFilters: {
            ...prev.studentListFilters,
            division: undefined,
          },
        }));
      }
    } else {
      setDivisionOptions([]);
      setPayload((prev: any) => ({
        ...prev,
        studentListFilters: {
          ...prev.studentListFilters,
          division: undefined,
        },
      }));
    }
  }, [divisionData, divisionValue, setPayload]);

  const { loading, data, fetchData, error } = useQuery("/admin/students", {
    params: {
      ...payload,
      className: className || undefined,
      division: divisionValue || undefined,
    },
  });

  useEffect(() => {
    fetchData();
  }, [className, divisionValue]);

  const studentData = data as any;

  const renderStudentCell = (row: any, col: string) => {
    const sc = row?.studentClass?.[0];
    switch (col) {
      case "Class":
        return sc?.class?.name ?? "-";
      case "division":
        return sc?.division ?? "-";
      case "academicYear":
        return sc?.academicYear?.name ?? "-";
      default:
        return row[col] ?? "-";
    }
  };

  const studentDisplayColumns = [
    "admissionNumber",
    "name",
    "Class",
    "division",
    "academicYear",
  ];

  const titleBarNode = <SearchBox />;

  const titleBar = {
    title: "Student List",
    headerTitle: "Student List",
    titleBarNodes: (studentData?.totalItems || payload.search) && titleBarNode,
    subHeading: "",
    rowCount: studentData?.totalItems,
  };

  const hasSearch = !!payload.search;
  const hasFilters = !!className || !!divisionValue;
  const hasData = (studentData?.items?.length ?? 0) > 0;

  return (
    <TableViewContainer titleBar={titleBar}>
      <div
        style={{
          display: "flex",
          gap: "1rem",
          marginTop:
            !hasData && !hasSearch && !hasFilters
              ? "1rem"
              : hasSearch
              ? "-3.85rem"
              : hasFilters && !hasData
              ? "1rem"
              : hasFilters && hasData
              ? "-3.85rem"
              : "-3.85rem",
          marginLeft: "2rem",
          transition: "margin-top 0.3s ease-in-out",
        }}
      >
        <div style={{ minWidth: "200px" }}>
          <ZSSelectBox
            fieldProps={{
              id: "className",
              name: "className",
              placeholder: "Select Class",
            }}
            options={classOptions}
            value={className}
            onChange={(value: string) =>
              setPayload({
                ...payload,
                studentListFilters: {
                  className: value || undefined,
                  division: undefined,
                },
              })
            }
          />
        </div>
        <div style={{ minWidth: "200px" }}>
          <ZSSelectBox
            fieldProps={{
              id: "division",
              name: "division",
              placeholder: "Select Division",
            }}
            options={divisionOptions}
            value={divisionValue}
            onChange={(value: string) =>
              setPayload({
                ...payload,
                studentListFilters: {
                  ...payload.studentListFilters,
                  division: value || undefined,
                },
              })
            }
            disabled={!className}
          />
        </div>
        <button
          style={{
            height: 45,
            padding: "1rem",
            backgroundColor: "primary.dark",
            border: "1px solid #ccc",
            borderRadius: 16,
            cursor: "pointer",
            minWidth: "200px",
            color: "#666666",
          }}
          onClick={() => {
            setPayload({
              ...payload,
              studentListFilters: {
                className: undefined,
                division: undefined,
              },
              skip: 0,
            });
          }}
        >
          Clear Filters
        </button>
      </div>

      {loading && <Loader />}
      {(error as any)?.statusCode === 403 && <NoPermissionContent />}
      {!loading && !error && (
        <DataTable
          entityName="Students"
          data={studentData?.items ?? []}
          displayColumns={studentDisplayColumns}
          linkColumn="name"
          linkUrl="/students/student-list"
          pagination={{ take: payload.take, skip: payload.skip }}
          setPagination={setPayload}
          totalItems={studentData?.totalItems}
          refetchFunction={fetchData}
          renderCell={renderStudentCell}
        />
      )}
    </TableViewContainer>
  );
};

export default StudentsList;
